import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  IconButton,
  Grid,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  AutoAwesome,
  FavoriteBorder,
  TrendingUp,
  WbSunny,
  Login as LoginIcon,
  PersonAdd,
} from '@mui/icons-material';

// ----------------------------------------------------------------
// 輪播資料型別
// ----------------------------------------------------------------
interface Slide {
  title: string;
  subtitle: string;
  accent: string;
  preview: React.ReactNode;
}

const SLIDE_INTERVAL_MS = 5000;

// ================================================================
// 主功能輪播預覽元件
// ================================================================

const ChartPreview: React.FC = () => {
  const palaces = [
    ['巳', '紫微', '天相'],
    ['午', '天機', ''],
    ['未', '太陽', '化祿'],
    ['申', '武曲', '七殺'],
    ['辰', '天同', ''],
    null,
    null,
    ['酉', '太陰', ''],
    ['卯', '貪狼', '化科'],
    null,
    null,
    ['戌', '天梁', ''],
    ['寅', '廉貞', '天府'],
    ['丑', '破軍', ''],
    ['子', '巨門', ''],
    ['亥', '天同', '化忌'],
  ];
  const cell = (idx: number) => {
    const d = palaces[idx];
    if (!d) return null;
    return (
      <Box sx={{ p: 0.3, fontSize: '9px', lineHeight: 1.2 }}>
        <Typography sx={{ fontSize: '8px', color: '#999', fontWeight: 600 }}>{d[0]}</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#7B1FA2' }}>{d[1]}</Typography>
        {d[2] && <Chip label={d[2]} size="small" sx={{ height: 14, fontSize: '7px', bgcolor: '#EDE7F6', mt: 0.2 }} />}
      </Box>
    );
  };
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(4, 44px)', gap: '1px', bgcolor: '#bbb', borderRadius: 1, overflow: 'hidden', width: 240, mx: 'auto' }}>
      {[0,1,2,3,4,null,null,7,8,null,null,11,12,13,14,15].map((v, i) => {
        if (v === null) return null;
        return (
          <Box key={i} sx={{ bgcolor: i === 3 ? '#FCE4EC' : '#FAFAFA', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            {cell(v)}
          </Box>
        );
      })}
      <Box sx={{ gridColumn: '2 / 4', gridRow: '2 / 4', bgcolor: '#F3E5F5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1, gridArea: 'unset' }} />
    </Box>
  );
};

const QAPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto', textAlign: 'left' }}>
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1565C0', mb: 0.5 }}>今年感情運勢如何？</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8 }}>
        {['MAIN', 'NTD 99'].map(t => <Chip key={t} label={t} size="small" sx={{ fontSize: '9px', height: 16 }} />)}
      </Box>
      <Typography sx={{ fontSize: '10px', color: '#555', lineHeight: 1.5 }}>
        ## 感情運勢總覽<br />
        夫妻宮有<b>天機化祿</b>入駐，今年在感情方面…整體而言桃花運旺盛，但需避免衝動…
      </Typography>
      <Box sx={{ mt: 0.8, p: 0.8, bgcolor: '#E8F5E9', borderRadius: 1 }}>
        <Typography sx={{ fontSize: '9px', color: '#2E7D32' }}>綜合評分：8.2 / 10</Typography>
      </Box>
    </Paper>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1565C0', mb: 0.5 }}>今年財運分析</Typography>
      <Typography sx={{ fontSize: '10px', color: '#555' }}>財帛宮武曲七殺，偏財機會多…</Typography>
    </Paper>
  </Box>
);

const PairPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#C62828', mb: 1, textAlign: 'center' }}>感情配對分析</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', fontSize: '18px' }}>A</Box>
          <Typography sx={{ fontSize: '9px', mt: 0.3 }}>1990/05/12</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '16px', color: '#E91E63' }}>❤</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#C62828' }}>82分</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#FCE4EC', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', fontSize: '18px' }}>B</Box>
          <Typography sx={{ fontSize: '9px', mt: 0.3 }}>1993/08/22</Typography>
        </Box>
      </Box>
      <Box sx={{ bgcolor: '#FFF3E0', borderRadius: 1, p: 0.8 }}>
        <Typography sx={{ fontSize: '9px', color: '#E65100' }}>夫妻宮互動佳，雙方命宮互映，長期穩定發展的好組合。</Typography>
      </Box>
    </Paper>
  </Box>
);

const DailyPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1, color: '#2E7D32' }}>今日全人建議</Typography>
      {[
        { icon: '🍱', label: '飲食', desc: '適合清淡蔬食，避免油炸' },
        { icon: '👕', label: '穿搭', desc: '藍色或綠色系，提升官祿' },
        { icon: '📚', label: '學習', desc: 'Python 第 3 課：函數' },
        { icon: '🏃', label: '運動', desc: '有氧步行 30 分鐘' },
        { icon: '💭', label: '自省', desc: '今天遇到挫折時，你的第一反應是？' },
      ].map(row => (
        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
          <Typography sx={{ fontSize: '12px', width: 18 }}>{row.icon}</Typography>
          <Chip label={row.label} size="small" sx={{ fontSize: '8px', height: 14, width: 36 }} />
          <Typography sx={{ fontSize: '9px', color: '#555', flex: 1 }}>{row.desc}</Typography>
        </Box>
      ))}
    </Paper>
  </Box>
);

// ================================================================
// 系統特色輪播預覽元件
// ================================================================

const LuckyElementsPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1, color: '#E65100', textAlign: 'center' }}>今日幸運元素</Typography>
      {[
        { label: '幸運數字', value: '3 · 8 · 21' },
        { label: '幸運方位', value: '東南方' },
        { label: '幸運顏色', value: '靛藍 / 金色' },
        { label: '今日最旺宮', value: '事業宮' },
      ].map(row => (
        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.7 }}>
          <Chip label={row.label} size="small" sx={{ fontSize: '8px', height: 16, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '10px', color: '#555', ml: 1 }}>{row.value}</Typography>
        </Box>
      ))}
      <Box sx={{ mt: 1, p: 0.8, bgcolor: '#FFF8E1', borderRadius: 1 }}>
        <Typography sx={{ fontSize: '9px', color: '#795548', lineHeight: 1.5 }}>
          生活參考用，非投機工具。<br />天道運行，順勢而為才是根本。
        </Typography>
      </Box>
    </Paper>
  </Box>
);

const ShareQRPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1, color: '#6A1B9A', textAlign: 'center' }}>分享我的命盤</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
        <Box sx={{ width: 60, height: 60, bgcolor: '#F3E5F5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '2px solid #CE93D8' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 8px)', gridTemplateRows: 'repeat(5, 8px)', gap: '1.5px' }}>
            {[1,1,1,1,0, 1,0,1,0,1, 1,1,0,1,1, 0,1,0,0,1, 1,0,1,1,0].map((v, i) => (
              <Box key={i} sx={{ bgcolor: v ? '#7B1FA2' : 'transparent', borderRadius: '1px' }} />
            ))}
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '9px', color: '#888', mb: 0.5 }}>自選公開欄位：</Typography>
          {['命盤格局圖', '命宮主星', '目前大限'].map((f, i) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', border: '1.5px solid #7B1FA2', bgcolor: i < 2 ? '#7B1FA2' : 'transparent', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '10px', color: '#333' }}>{f}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 0.8, bgcolor: '#EDE7F6', borderRadius: 1 }}>
        <Typography sx={{ fontSize: '9px', color: '#4A148C', lineHeight: 1.4 }}>
          設定密碼保護，對方需輸入密碼才能查看你的命盤
        </Typography>
      </Box>
    </Paper>
  </Box>
);

const PairTipPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1, color: '#AD1457', textAlign: 'center' }}>配對分析小技巧</Typography>
      <Box sx={{ p: 0.8, bgcolor: '#FCE4EC', borderRadius: 1, mb: 1 }}>
        <Typography sx={{ fontSize: '10px', color: '#880E4F', fontWeight: 600, mb: 0.3 }}>不知道對方生辰？</Typography>
        <Typography sx={{ fontSize: '9px', color: '#555', lineHeight: 1.5 }}>直接問對方就好！配對頁面由你輸入雙方資料，對方不需要有帳號。</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {[
          { label: '你', bg: '#E3F2FD', color: '#1565C0', text: '填寫自己的出生資料' },
          { label: '對方', bg: '#FCE4EC', color: '#C62828', text: '告知年月日時，你來輸入' },
        ].map(r => (
          <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, p: 0.6, bgcolor: r.bg, borderRadius: 1 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: r.color, minWidth: 30 }}>{r.label}</Typography>
            <Typography sx={{ fontSize: '9px', color: '#555' }}>{r.text}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  </Box>
);

const HRPreview: React.FC = () => (
  <Box sx={{ width: 240, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1, color: '#1565C0', textAlign: 'center' }}>人資篩選模式（五選一）</Typography>
      {[
        { name: '候選人 A', score: 91, barColor: '#43A047' },
        { name: '候選人 B', score: 78, barColor: '#FB8C00' },
        { name: '候選人 C', score: 65, barColor: '#E53935' },
      ].map((c, i) => (
        <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.7 }}>
          <Typography sx={{ fontSize: '9px', color: '#888', width: 14 }}>#{i + 1}</Typography>
          <Typography sx={{ fontSize: '10px', flex: 1 }}>{c.name}</Typography>
          <Box sx={{ width: 50, height: 6, bgcolor: '#EEE', borderRadius: 3 }}>
            <Box sx={{ width: `${c.score}%`, height: '100%', bgcolor: c.barColor, borderRadius: 3 }} />
          </Box>
          <Typography sx={{ fontSize: '9px', fontWeight: 700, minWidth: 20, textAlign: 'right' }}>{c.score}</Typography>
        </Box>
      ))}
      <Box sx={{ p: 0.8, bgcolor: '#E3F2FD', borderRadius: 1, mt: 0.5 }}>
        <Typography sx={{ fontSize: '9px', color: '#0D47A1' }}>命理角度提供客觀參考，協助決策</Typography>
      </Box>
    </Paper>
  </Box>
);

// ================================================================
// 輪播資料
// ================================================================

const featuresSlides: Slide[] = [
  {
    title: '完整命盤格局圖',
    subtitle: '直覺宮格排列，一眼看清天干地支與主星分佈，讓命盤不再是文字謎題',
    accent: '#7B1FA2',
    preview: <ChartPreview />,
  },
  {
    title: '個人專屬運勢解說',
    subtitle: '感情、財運、事業全方位解說，每份報告依你的命盤量身分析——如同專屬諮詢師隨時在側，解說的是你自己的命盤',
    accent: '#1565C0',
    preview: <QAPreview />,
  },
  {
    title: '精準配對分析',
    subtitle: '感情、事業、友誼多種情境，讓你洞察關係本質、避開盲點；也適用於公司人資篩選人才，找出最契合團隊的候選人',
    accent: '#C62828',
    preview: <PairPreview />,
  },
  {
    title: '每日生活教練',
    subtitle: '飲食、穿搭、學習、運動、自省——命盤驅動的個人化日常建議，讓你天天回來用',
    accent: '#2E7D32',
    preview: <DailyPreview />,
  },
];

const systemSlides: Slide[] = [
  {
    title: '每日幸運元素',
    subtitle: '在食衣住行一時不知所措，或想投石問路時，每日幸運數字、方位、顏色能給你一個生活參考。注意：幸運數字是方向提示，拿去投機賭博是無效的——華人講究天道運行，順勢而為才是根本。',
    accent: '#E65100',
    preview: <LuckyElementsPreview />,
  },
  {
    title: '命盤 QR Code 分享',
    subtitle: '把自己的命盤分享給朋友或有緣人，自選要公開的資訊欄位，並設定密碼保護。對方收到 QR Code 後，需輸入密碼才能查看你的命盤資訊，隱私自己掌控。',
    accent: '#6A1B9A',
    preview: <ShareQRPreview />,
  },
  {
    title: '配對問題小技巧',
    subtitle: '進行配對分析時，如果不知道對方的生辰八字，直接請對方告訴你就好了。配對頁面由你同時輸入雙方的出生資料，對方不需要有帳號，簡單方便。',
    accent: '#AD1457',
    preview: <PairTipPreview />,
  },
  {
    title: '人資篩選應用',
    subtitle: '公司人資或主管不確定選誰比較好，可以使用「主管與屬下配對」模式——一次最多五選一，從命理角度提供客觀參考，讓你做出更有把握的決策。',
    accent: '#1565C0',
    preview: <HRPreview />,
  },
];

// ================================================================
// 可重用輪播元件
// ================================================================

const FeatureCarousel: React.FC<{ slides: Slide[] }> = ({ slides }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((idx: number) => {
    setFading(true);
    setTimeout(() => {
      setActiveSlide((idx + slides.length) % slides.length);
      setFading(false);
    }, 220);
  }, [slides.length]);

  useEffect(() => {
    const t = setInterval(() => goTo(activeSlide + 1), SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [activeSlide, goTo]);

  const slide = slides[activeSlide];

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
        }}
      >
        <Grid container>
          <Grid item xs={12} md={5} sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: 320 } }}>
            <Box sx={{ opacity: fading ? 0 : 1, transition: 'opacity 0.22s ease' }}>
              <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: slide.accent, mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mb: 1.5, fontSize: { xs: '1.2rem', md: '1.4rem' } }}>
                {slide.title}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                {slide.subtitle}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={7} sx={{ p: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.03)', borderLeft: { md: '1px solid rgba(255,255,255,0.08)' }, minHeight: { xs: 240, md: 320 } }}>
            <Box sx={{ opacity: fading ? 0 : 1, transition: 'opacity 0.22s ease', transform: fading ? 'translateY(6px)' : 'translateY(0)', transitionTimingFunction: 'ease' }}>
              {slide.preview}
            </Box>
          </Grid>
        </Grid>
        <IconButton onClick={() => goTo(activeSlide - 1)} size="small" sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, display: { xs: 'none', md: 'flex' } }}>
          <ArrowBackIos sx={{ fontSize: 14, pl: 0.5 }} />
        </IconButton>
        <IconButton onClick={() => goTo(activeSlide + 1)} size="small" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, display: { xs: 'none', md: 'flex' } }}>
          <ArrowForwardIos sx={{ fontSize: 14 }} />
        </IconButton>
      </Paper>

      {/* 點點指示器 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2.5 }}>
        {slides.map((_, i) => (
          <Box
            key={i}
            onClick={() => goTo(i)}
            sx={{
              width: i === activeSlide ? 24 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: i === activeSlide ? slide.accent : 'rgba(255,255,255,0.25)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </>
  );
};

// ================================================================
// 新手入門指南資料
// ================================================================

const NEW_USER_TIPS = [
  {
    num: '01',
    title: '生辰八字務必正確',
    desc: '出生年、月、日、時一定要填正確，這樣系統才能幫你算到自己的命盤，而不是算到別人頭上去了。',
    color: '#E53935',
  },
  {
    num: '02',
    title: '不確定生辰？去戶政查',
    desc: '如果不確定自己正確的出生時辰，可以去戶政事務所申請出生證明書影本，上面有完整的出生資料。',
    color: '#1565C0',
  },
  {
    num: '03',
    title: '姓名／代稱不用填真名',
    desc: '姓名欄可以填代稱，不需要真實姓名。如果你很重視隱私，自取一個代稱就好，這樣就沒有人知道你是誰了。',
    color: '#2E7D32',
  },
  {
    num: '04',
    title: 'Email 與電話要填真實的',
    desc: 'Email 和電話必須是真實可用的，才能收到驗證碼完成註冊，日後也方便接收系統重要公告。',
    color: '#6A1B9A',
  },
];

// ================================================================
// 主元件
// ================================================================

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0A0A1A' }}>

      {/* ---- 頂部導覽列 ---- */}
      <Box sx={{ px: { xs: 2, md: 6 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ color: '#FFD700', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
            我的好運氣
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            登入
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/register')}
            sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', fontWeight: 700 }}
          >
            新使用者註冊
          </Button>
        </Box>
      </Box>

      {/* ---- Hero 區 ---- */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,126,234,0.25) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {['個人專屬報告', '全方位運勢解說', '配對分析', '每日生活建議'].map(tag => (
              <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(102,126,234,0.25)', color: '#a5b4fc', border: '1px solid rgba(102,126,234,0.4)', fontSize: '11px' }} />
            ))}
          </Box>
          <Typography variant="h2" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.15, mb: 1 }}>
            紫微斗數<br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #667eea, #f093fb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              智慧解析系統
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: { xs: '0.95rem', md: '1.1rem' }, mb: 4, maxWidth: 500, mx: 'auto' }}>
            依據你的命盤，每天給你<b style={{ color: '#c4b5fd' }}>飲食、穿搭、學習</b>個人化建議，<br />隨時查詢<b style={{ color: '#c4b5fd' }}>感情、財運、事業</b>深度分析
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', fontWeight: 700, px: 4, py: 1.5, fontSize: '1rem', borderRadius: 3 }}
            >
              新使用者註冊
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', px: 4, py: 1.5, fontSize: '1rem', borderRadius: 3, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: '#fff' } }}
            >
              已有帳號登入
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ---- 四大核心功能輪播 ---- */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: '#0D0D1F' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', mb: 4 }}>
            四大核心功能
          </Typography>
          <FeatureCarousel slides={featuresSlides} />

          {/* 功能標籤橫排 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 3 }, mt: 5, flexWrap: 'wrap' }}>
            {[
              { icon: <AutoAwesome sx={{ fontSize: 18 }} />, label: '命盤格局圖', color: '#7B1FA2' },
              { icon: <TrendingUp sx={{ fontSize: 18 }} />, label: '個人專屬解說', color: '#1565C0' },
              { icon: <FavoriteBorder sx={{ fontSize: 18 }} />, label: '配對分析', color: '#C62828' },
              { icon: <WbSunny sx={{ fontSize: 18 }} />, label: '每日建議', color: '#2E7D32' },
            ].map(item => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'rgba(255,255,255,0.55)' }}>
                <Box sx={{ color: item.color }}>{item.icon}</Box>
                <Typography sx={{ fontSize: '0.85rem' }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ---- 系統特色輪播 ---- */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: '#0A0A1A' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', mb: 1 }}>
            使用前你想知道的事
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', textAlign: 'center', fontSize: '0.9rem', mb: 4 }}>
            關於這個系統，你可能會好奇的幾件事
          </Typography>
          <FeatureCarousel slides={systemSlides} />
        </Container>
      </Box>

      {/* ---- 新手入門指南 ---- */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: '#0D0D1F' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', mb: 1 }}>
            新手入門指南
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', textAlign: 'center', fontSize: '0.9rem', mb: 4 }}>
            第一次使用，先看這四件事
          </Typography>
          <Grid container spacing={2}>
            {NEW_USER_TIPS.map(tip => (
              <Grid item xs={12} sm={6} key={tip.num}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${tip.color}44`,
                    height: '100%',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ minWidth: 38, height: 38, borderRadius: 2, bgcolor: `${tip.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${tip.color}55`, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 900, color: tip.color, lineHeight: 1 }}>{tip.num}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>{tip.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.65 }}>{tip.desc}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ---- 底部 CTA ---- */}
      <Box sx={{ py: 5, textAlign: 'center', background: 'linear-gradient(135deg, #302b63, #24243e)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
          準備好了解自己了嗎？
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 3, fontSize: '0.95rem' }}>
          立即開始你專屬的命盤解析旅程
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/register')}
          sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', fontWeight: 700, px: 5, py: 1.5, borderRadius: 3, fontSize: '1rem' }}
        >
          立即註冊使用
        </Button>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 3, fontSize: '0.85rem' }}>
          有任何問題？請聯絡{' '}
          <MuiLink href="mailto:howyun.service@zetaaxis.com" sx={{ color: 'rgba(255,255,255,0.65)', textDecorationColor: 'rgba(255,255,255,0.4)' }}>
            howyun.service@zetaaxis.com
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default LandingPage;
